import React, { useState, useEffect } from "react";
import { Card, Headline, Paragraph, Flex, Button, Loading } from "_styleguide";
import { hexToRgba, formatMoney } from "_assets/js/helpers";
import { previewPlans } from "./_helpers";
import { getPlans } from "_graphql/queries";
import { useApolloClient } from "@apollo/client";
import { useLead } from "_contexts/useLead";
import { useSettings } from "_contexts/useSettings";
import { selectPlan } from "_graphql/mutations";
import { message } from "@centrate-io/barn";
import { useNavigate } from "react-router-dom";
import { useStats } from "_contexts/useStats";
import "./Plan.scss";

const COSTS = {
  service_percent: 0.4,
  monthly_fees: 50,
  months: 24,
  default_preview: 10000,
};

function Plan(props) {
  const pageSettings = props.settings?.settings;
  const client = useApolloClient();
  const navigate = useNavigate();
  const { lead, reload } = useLead();
  const { isPreview, settings } = useSettings();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { updateStats } = useStats();

  const fetchPlans = async () => {
    const queryData = await client.query({
      variables: {
        application_id: lead?.id,
        company_id: localStorage.getItem('pp_company_id') || process.env.REACT_APP_COMPANY_ID,
      },
      query: getPlans,
    });
    const plans = queryData?.data?.getPlans || [];
    setLoading(false);
    setPlans(plans);
  };

  const choosePlan = async (planId) => {
    try {
      setSaving(planId);
      const response = await client.mutate({
        mutation: selectPlan,
        variables: {
          plan_id: planId,
          application_id: lead?.id,
        },
      });
      if (response?.data?.selectPlan?.id && props?.settings?.next) {
        await reload();
        await updateStats({
          application_id: lead.id,
          self_enrollment_id: settings.id,
          page: props?.settings?.name,
          drop_off_page: props?.settings?.next || props?.settings?.name,
        });
        navigate(`/${props?.settings?.next}`);
      } else {
        throw new Error();
      }
      setSaving(false);
    } catch (err) {
      message.error("Error selecting plan, try again");
      setSaving(false);
    }
  };

  const monthlyFees = (plan, totalDebt) => {
    let monthlyFees = 0;
    plan.fees.forEach((fee) => {
      if (fee.type === "percentage") {
        monthlyFees += parseFloat((totalDebt * fee.amount) / plan.months);
      } else if (fee.type === "flat") {
        monthlyFees += parseFloat(fee.amount);
      }
    });
    return monthlyFees;
  };

  const calculateCost = (plan) => {
    const totalDebt =
      lead?.plan?.debt_amount ||
      pageSettings?.default_preview ||
      COSTS.default_preview;
    const fees = monthlyFees(plan, totalDebt);
    return parseFloat((totalDebt * plan.rate) / plan.months + fees);
  };

  const planList =
    (pageSettings?.plan_ids?.length <= 0 || plans?.length <= 0) && isPreview
      ? previewPlans
      : plans
          .filter((plan) => pageSettings?.plan_ids.includes(plan.id))
          .sort(
            (a, b) =>
              pageSettings.plan_ids.indexOf(a.id) -
              pageSettings.plan_ids.indexOf(b.id)
          )
          .map((plan) => ({
            plan_name: plan.name,
            primary_color: plan.background_color,
            months: plan.months,
            rate: plan.rate,
            fees: plan.fees || [],
            id: plan.id,
          }));

  useEffect(() => {
    fetchPlans();
  }, []);

  return loading ? (
    <Card id="plan">
      <Loading />
    </Card>
  ) : (
    <Card id="plan">
      <div className="apply-form">
        <Headline>{pageSettings.title}</Headline>
        {pageSettings.subtitle ? (
          <Paragraph
            opacity={0.6}
            align="left"
            size="14px"
            dangerouslySetInnerHTML={{
              __html: pageSettings.subtitle,
            }}
          />
        ) : null}
        <Flex gap="12px" vertical style={{ marginTop: "16px" }}>
          {planList?.map((option, index) => {
            const actualBackgroundColor = hexToRgba(option.primary_color, 0.3);
            return (
              <div className="plan-option" key={`plan-option-${index}`}>
                <div className="plan-option-title">
                  <div
                    style={{
                      backgroundColor: actualBackgroundColor,
                      color: `${option.primary_color}`,
                    }}
                  >
                    <Paragraph size="12px">{option.plan_name}</Paragraph>
                  </div>
                </div>
                <Paragraph size="22px" className="plan-option-name">
                  {formatMoney(calculateCost(option))}
                  <span>/month</span>
                </Paragraph>
                <Paragraph
                  size="14px"
                  opacity={0.6}
                  className="plan-option-length"
                >
                  Debt free in {option.months} months
                </Paragraph>
                <Button
                  type="blanco"
                  block
                  size="small"
                  onClick={() => (isPreview ? null : choosePlan(option.id))}
                  disabled={saving && saving !== option.id}
                  loading={saving === option.id}
                >
                  Select Plan
                </Button>
              </div>
            );
          })}
        </Flex>
      </div>
    </Card>
  );
}

export default Plan;
